import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaBrain, 
  FaComments, 
  FaMicrochip,
  FaBolt, 
  FaStar, 
  FaRobot,
  FaArrowRight,
  FaUserCircle,
  FaEnvelope,
  FaFileAlt,
  FaCog,
  FaArrowsAltH,
  FaShieldAlt,
  FaExclamationTriangle,
  FaNetworkWired
} from 'react-icons/fa';

const AIAgentsPage = () => {
  const [activeAgent, setActiveAgent] = useState(null);
  const navigate = useNavigate();

  const features = [
    {
      icon: <FaBolt className="w-6 h-6 text-amber-400" />,
      title: "Lightning Fast",
      description: "Our agents process requests in milliseconds, ensuring low latency response times."
    },
    {
      icon: <FaStar className="w-6 h-6 text-purple-400" />,
      title: "Highly Accurate",
      description: "Advanced algorithms ensure precise and reliable results every time."
    },
    {
      icon: <FaRobot className="w-6 h-6 text-blue-400" />,
      title: "Feels like Human",
      description: "AI powered phone calls sound remarkably human like, engaging in natural conversations and providing responses with a conversational flow."
    }
  ];

  // Handler for Get Started buttons
  const handleGetStarted = () => {
    navigate('/enquiry');
  };

  // Data from AIAgentsEcosystem.js for the middle section
  const agents = [
    {
      id: 1,
      name: "Voice Agent",
      icon: <FaComments className="w-6 h-6 text-purple-500" />,
      description: "Engages in natural, context-aware conversations with users",
      capabilities: ["Natural Language Processing", "Context Understanding", "Multi-lingual Support", "Sentiment Analysis"],
      useCase: "Ideal for customer service and support automation",
      position: "secondary"
    },
    {
      id: 2,
      name: "Text Agent",
      icon: <FaFileAlt className="w-6 h-6 text-indigo-500" />,
      description: "Processes and generates text-based responses with high accuracy",
      capabilities: ["Text Analysis", "Content Generation", "Language Translation", "Contextual Responses"],
      useCase: "Perfect for content creation and text processing tasks",
      position: "secondary"
    },
    {
      id: 3,
      name: "Email Agent",
      icon: <FaEnvelope className="w-6 h-6 text-blue-500" />,
      description: "Manages email communications with intelligent processing",
      capabilities: ["Email Drafting", "Response Prioritization", "Content Analysis", "Automatic Categorization"],
      useCase: "Streamlines email management and communication",
      position: "secondary"
    },
    {
      id: 4,
      name: "LLM Core",
      icon: <FaBrain className="w-6 h-6 text-blue-500" />,
      description: "Central intelligence that powers all agent capabilities",
      capabilities: ["Deep Learning", "Pattern Recognition", "Reasoning", "Natural Language Understanding"],
      useCase: "Forms the foundation of all AI functionalities",
      position: "core"
    }
  ];

  const systems = [
    {
      id: "memory",
      name: "Memory",
      icon: <FaCog className="w-5 h-5 text-blue-400" />,
      description: "Stores conversation context and important information",
      details: [
        "Short-term memory (e.g. train of thought)",
        "Long-term memory or conversation history and context",
        "Information synthesis and retention"
      ]
    },
    {
      id: "custom-tools",
      name: "Custom Tools",
      icon: <FaNetworkWired className="w-5 h-5 text-purple-400" />,
      description: "External integrations that enhance agent capabilities",
      details: [
        "APIs to connect to 3rd party systems",
        "Search API, Weather API",
        "RAG pipeline integration",
        "Custom function calling"
      ]
    },
    {
      id: "customer-context",
      name: "Customer Context",
      icon: <FaUserCircle className="w-5 h-5 text-indigo-400" />,
      description: "Maintains user profiles and interaction history",
      details: [
        "Customer profile data",
        "Interaction preferences",
        "Historical conversation patterns",
        "Personalization insights"
      ]
    },
    {
      id: "rag-pipeline",
      name: "RAG Pipeline",
      icon: <FaNetworkWired className="w-5 h-5 text-green-400" />,
      description: "Retrieval-Augmented Generation for accurate responses",
      details: [
        "Info retrieval from various sources",
        "Knowledge integration",
        "Dynamic content generation",
        "Real-time data augmentation"
      ]
    }
  ];

  const handleToggleDetails = (id) => {
    setActiveAgent(activeAgent === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50">
      {/* Hero Section with improved background */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-100 via-indigo-100 to-purple-200">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-30">
          <div className="absolute w-96 h-96 rounded-full bg-pink-300 blur-3xl -top-20 -left-20"></div>
          <div className="absolute w-96 h-96 rounded-full bg-indigo-300 blur-3xl bottom-0 right-0"></div>
          <div className="absolute w-64 h-64 rounded-full bg-blue-300 blur-3xl top-1/2 left-1/3"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative">
          <div className="text-center">
            {/* Title with Aurora Animation - Fixed to prevent text cutting */}
            <div className="overflow-visible mb-6">
              <h1 className="text-5xl font-bold text-indigo-900">
                <span className="aurora-text-animation inline-block" style={{lineHeight: "1.3", padding: "4px 0"}}>
                  Next-Generation AI Agents
                </span>
              </h1>
            </div>
            <p className="text-xl text-indigo-700 mb-8 max-w-2xl mx-auto">
              Empower your business with intelligent AI agents that transform the way you work. 
              Experience the future of automation and decision-making.
            </p>
            <button 
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-indigo-400 to-purple-400 text-white px-8 py-3 rounded-lg font-medium hover:from-indigo-500 hover:to-purple-500 transition-all duration-300 inline-flex items-center shadow-lg"
            >
              Get Started <FaArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Wave separator - lighter color */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-16 text-blue-50 fill-current">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"></path>
          </svg>
        </div>
      </div>

      {/* Features Section - Improved Connected Card Design */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="relative">
          {/* Connected lines between cards - Improved */}
          <div className="absolute top-1/2 left-0 w-full h-1 bg-gradient-to-r from-purple-200 via-blue-200 to-indigo-200 transform -translate-y-1/2 hidden md:block rounded-full shadow-sm"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-white to-blue-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-indigo-100 relative z-10 group hover:border-indigo-200"
              >
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-indigo-800">{feature.title}</h3>
                <p className="text-indigo-600">{feature.description}</p>
                
                {/* Connection nodes - Lighter colors */}
                <div className="absolute left-1/2 top-1/2 w-6 h-6 bg-gradient-to-br from-purple-200 to-indigo-200 rounded-full transform -translate-x-1/2 -translate-y-1/2 hidden md:flex items-center justify-center border-2 border-white shadow-sm group-hover:scale-125 transition-transform duration-300">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
                
                {/* Hover indicator line - lighter */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-1 bg-gradient-to-r from-purple-300 to-indigo-300 group-hover:w-3/4 transition-all duration-300 rounded-full"></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Agent Ecosystem Diagram from AIAgentsEcosystem.js */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-3xl font-bold text-center mb-16 text-indigo-900 relative">
          <span className="relative z-10">Our AI Agents</span>
          <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-purple-400 to-indigo-400 rounded-full"></span>
        </h2>

        <div className="relative bg-white rounded-2xl shadow-xl p-8 mb-16 overflow-hidden">
          {/* Improved Background Elements */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
            <div className="absolute w-96 h-96 rounded-full bg-indigo-300 blur-3xl -top-20 -left-20 animate-blob"></div>
            <div className="absolute w-96 h-96 rounded-full bg-purple-300 blur-3xl bottom-0 right-0 animate-blob animation-delay-2000"></div>
            <div className="absolute w-96 h-96 rounded-full bg-blue-300 blur-3xl top-1/3 right-1/4 animate-blob animation-delay-4000"></div>
          </div>
          
          <div className="relative z-10">
            {/* Customer Query Start */}
            <div className="flex items-center justify-start mb-12">
              <div className="bg-indigo-100 rounded-lg p-4 flex items-center max-w-sm transition-all duration-300 hover:shadow-md hover:bg-indigo-50">
                <div className="w-12 h-12 bg-indigo-200 rounded-full mr-4 flex items-center justify-center">
                  <FaUserCircle className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-bold text-indigo-800">Customer Query</h3>
                  <p className="text-sm text-indigo-600">Starting point of user interaction</p>
                </div>
              </div>
              <div className="h-1 bg-gradient-to-r from-indigo-500 to-purple-500 w-16 ml-4"></div>
            </div>

            {/* Enhanced Main System Layout */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {/* Left Column - LLM Core with animation */}
              <div className="bg-indigo-600 text-white rounded-xl p-6 shadow-lg transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1 relative overflow-hidden">
                <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-indigo-500 rounded-full opacity-30"></div>
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-indigo-600/50 via-transparent to-transparent"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4 animate-pulse">
                    <FaBrain className="w-8 h-8 text-white" />
                  </div>
                  
                  <h2 className="text-2xl font-bold mb-4">LLM Core</h2>
                  
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-indigo-300 rounded-full mr-2"></div>
                      <span>Objective or Purpose</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-indigo-300 rounded-full mr-2"></div>
                      <span>Functionality</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-indigo-300 rounded-full mr-2"></div>
                      <span>Guardrails</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-indigo-300 rounded-full mr-2"></div>
                      <span>Persona</span>
                    </li>
                  </ul>
                  
                  <div className="mt-6 pt-4 border-t border-indigo-500">
                    <p className="text-indigo-200 text-sm">
                      The central intelligence hub that powers all agent capabilities and ensures coherent responses
                    </p>
                  </div>
                </div>
                
                {/* Enhanced Connection Lines */}
                <div className="hidden md:block absolute -right-8 top-1/2 w-8 h-1 bg-indigo-400 transform -translate-y-1/2 animate-pulse"></div>
              </div>
              
              {/* Middle Column - Support Systems with improved interactivity */}
              <div className="space-y-4">
                {systems.map((system, index) => (
                  <div 
                    key={system.id}
                    className={`bg-white border ${activeAgent === system.id ? 'border-purple-300' : 'border-purple-100'} rounded-lg p-4 shadow hover:shadow-md transition-all duration-300 relative transform ${activeAgent === system.id ? 'scale-102' : ''}`}
                  >
                    <h3 className="text-lg font-semibold text-indigo-800 flex items-center">
                      <div className={`w-8 h-8 rounded-full ${activeAgent === system.id ? 'bg-indigo-200' : 'bg-indigo-100'} flex items-center justify-center mr-3 transition-all duration-300`}>
                        {system.icon}
                      </div>
                      {system.name}
                    </h3>
                    
                    <p className="text-sm text-indigo-600 mt-1 mb-2">{system.description}</p>
                    
                    <div className={`overflow-hidden transition-all duration-300 ${activeAgent === system.id ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                      <ul className="pl-4 pt-2 space-y-1 text-sm text-indigo-700">
                        {system.details.map((detail, idx) => (
                          <li key={idx} className="flex items-center">
                            <div className="w-1 h-1 bg-purple-400 rounded-full mr-2"></div>
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <button 
                      className={`absolute bottom-2 right-2 ${activeAgent === system.id ? 'text-purple-600' : 'text-indigo-500'} text-xs p-1 focus:outline-none hover:underline`}
                      onClick={() => handleToggleDetails(system.id)}
                    >
                      {activeAgent === system.id ? 'Hide' : 'Details'}
                    </button>
                    
                    {/* Improved Connection Lines */}
                    <div className="hidden md:block absolute -left-8 top-1/2 w-8 h-1 bg-gradient-to-r from-indigo-300 to-indigo-400 transform -translate-y-1/2"></div>
                    <div className="hidden md:block absolute -right-8 top-1/2 w-8 h-1 bg-gradient-to-r from-indigo-400 to-indigo-300 transform -translate-y-1/2"></div>
                  </div>
                ))}
              </div>
              
              {/* Right Column - Agents with improved styling */}
              <div className="space-y-4">
                <div className="bg-purple-100 rounded-lg p-4 mb-4 hover:shadow-md transition-all duration-300">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center mr-3">
                      <FaStar className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="font-bold text-purple-800">Primary Agent</h3>
                  </div>
                  
                  <div className="bg-white rounded-lg shadow p-3 flex items-center hover:shadow-md transition-all duration-300 hover:bg-purple-50">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-2">
                      <FaRobot className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-indigo-800">Response Agent</h4>
                      <p className="text-xs text-indigo-600">Generates final output</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-indigo-100 rounded-lg p-4 hover:shadow-md transition-all duration-300">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-200 flex items-center justify-center mr-3">
                      <FaNetworkWired className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="font-bold text-indigo-800">Secondary Agents</h3>
                  </div>
                  
                  <div className="space-y-2">
                    {agents.filter(agent => agent.position === "secondary").map((agent, idx) => (
                      <div 
                        key={idx} 
                        className="bg-white rounded-lg shadow p-3 flex items-center hover:shadow-md transition-all duration-300 hover:bg-indigo-50"
                      >
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mr-2">
                          {agent.icon}
                        </div>
                        <div>
                          <h4 className="font-medium text-indigo-800">{agent.name}</h4>
                          <p className="text-xs text-indigo-600">{agent.capabilities[0]}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Connection Lines */}
                <div className="hidden md:block absolute -left-8 top-1/2 w-8 h-1 bg-indigo-400 transform -translate-y-1/2"></div>
              </div>
            </div>
            
            {/* Agent Response End */}
            <div className="flex items-center justify-end">
              <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-500 w-16 mr-4"></div>
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-4 flex items-center max-w-sm text-white hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-white/20 rounded-full mr-4 flex items-center justify-center animate-pulse">
                  <FaRobot className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold">Agent Response</h3>
                  <p className="text-sm opacity-90">Intelligent, contextual output</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative mt-16">
        {/* Decorative element */}
        <div className="absolute left-0 top-0 w-full h-32 bg-gradient-to-b from-indigo-50 to-transparent pointer-events-none"></div>
        
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-3xl sm:rounded-t-[5rem] overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
            <div className="absolute -left-20 -top-20 w-80 h-80 bg-purple-400 rounded-full blur-3xl"></div>
            <div className="absolute right-0 bottom-0 w-96 h-96 bg-indigo-400 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 relative">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div className="text-left">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">Ready to Transform Your Business?</h2>
                <p className="text-lg text-indigo-100 mb-8 max-w-md">Start using our AI agents today and experience the difference. Boost productivity, reduce costs, and stay ahead of the competition.</p>
                <button 
                  onClick={handleGetStarted}
                  className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-medium hover:bg-indigo-50 transition-all duration-300 inline-flex items-center shadow-lg hover:shadow-xl group"
                >
                  Get Started Now <FaArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 bg-indigo-700/30 rounded-2xl backdrop-blur-sm transform rotate-6"></div>
                <div className="absolute inset-0 bg-purple-700/20 rounded-2xl backdrop-blur-sm transform -rotate-3"></div>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                  <div className="space-y-3">
                    <div className="h-4 bg-white/20 rounded-full w-full"></div>
                    <div className="h-4 bg-white/20 rounded-full w-4/5"></div>
                    <div className="h-4 bg-white/20 rounded-full w-2/3"></div>
                    <div className="h-4 bg-white/20 rounded-full w-1/2"></div>
                    <div className="h-20 bg-indigo-500/30 rounded-xl w-full mt-6 border border-indigo-400/30"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* CSS for Aurora Animation and other effects */}
      <style jsx>{`
        /* Aurora Text Animation */
        .aurora-text-animation {
          background-image: linear-gradient(
            92deg,
            #ff5e62 0%,
            #ff9966 20%, 
            #6a82fb 40%, 
            #05dfd7 60%, 
            #a770ef 80%,
            #ff5e62 100%
          );
          background-size: 200% auto;
          background-clip: text;
          text-fill-color: transparent;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: aurora 8s linear infinite;
          display: inline-block;
        }
        
        @keyframes aurora {
          to {
            background-position: 200% center;
          }
        }

        /* Blob Animation for Background Elements */
        @keyframes blob {
          0% { transform: translate(0%, 0%) scale(1); }
          33% { transform: translate(30%, -50%) scale(1.1); }
          66% { transform: translate(-20%, 20%) scale(0.9); }
          100% { transform: translate(0%, 0%) scale(1); }
        }
        
        .animate-blob {
          animation: blob 20s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default AIAgentsPage;